import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import Segmented from "../ui/segmented";
import { LatencyMeter } from "../ui/latency";
import { Fields, JsonView, PanelTitle } from "../ui/detail";
import TimeAgo from "../time-ago";
import { ApiLog, ApiLogDetail, policyOf } from "../../utils/api";
import PolicyChip from "./policy-chip";
import StepTrail from "./step-trail";
import { CATEGORY_LOOK, platformLabel } from "../../utils/status";
import { formatDateTime } from "../../utils/format-time";

const TABS = [
  { label: "Overview", value: "overview" },
  { label: "Request", value: "request" },
  { label: "Response", value: "response" },
];

const isRouteLog = (log: ApiLog) => log.link.startsWith("route:");
const logName = (log: ApiLog) => log.link.replace(/^[a-z]+:/, "");

const mono = (value: unknown) => (value == null || value === "" ? null : <span className="font-mono text-[12px]">{String(value)}</span>);

// ----------------------- EVERY FIELD OF ONE LOG, IN THREE TABS ------------------------------------
export default function LogDetail({ log }: { log: ApiLogDetail }) {
  const [tab, setTab] = useState("overview");
  const data = (log.data ?? {}) as Record<string, any>;
  const error = (data.error ?? null) as { code?: string; message?: string; stack?: string } | null;
  const request = data.request ?? { headers: data.headers, params: data.params, query: data.query, body: data.body };
  const response = data.response ?? null;
  const { error: _error, request: _request, response: _response, providerResponse, steps, responseTime: _time, headers: _h, params: _p, query: _q, body: _b, ...rest } = data;
  const related = log.related ?? [];
  const clientLog = isRouteLog(log) ? log : related.find(isRouteLog);
  const providerLogs = [log, ...related].filter((item) => !isRouteLog(item) && (item.data as any)?.providerResponse);
  const category = log.routeCategory ? CATEGORY_LOOK[log.routeCategory] : CATEGORY_LOOK.general;

  return (
    <>
      <Segmented label="Log details" value={tab} onChange={setTab} options={TABS} />

      {tab === "overview" ? (
        <>
          <div className="glass p-4"><LatencyMeter ms={log.responseTime} /></div>
          {error?.message ? (
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4">
              <p className="flex items-center gap-2 font-[Gilroy-Bold] text-[14px] text-rose-600 dark:text-rose-300">
                <FontAwesomeIcon icon={faCircleExclamation} />
                {error.code ?? "Error"}
              </p>
              <p className="mt-1.5 break-words font-[Gilroy-Medium] text-[14px] text-heading-color">{error.message}</p>
              {error.stack ? (
                <details className="mt-2">
                  <summary className="cursor-pointer font-[Gilroy-Medium] text-[12px] text-light-gray">Stack trace</summary>
                  <pre className="custom-scrollbar mt-2 overflow-auto font-mono text-[11px] text-light-gray">{error.stack}</pre>
                </details>
              ) : null}
            </div>
          ) : (
            <p className="font-[Gilroy-Medium] text-[14px] text-heading-color">{log.message}</p>
          )}
          <StepTrail steps={steps} failedAt={logName(log)} />
          <Fields
            rows={[
              ["Policy", policyOf(log) ? <PolicyChip value={policyOf(log)} /> : <span className="text-[12px] text-label-muted">not sent</span>, policyOf(log) ?? undefined],
              ["Platform", platformLabel(log.platform)],
              ["Status code", log.statusCode],
              ["Category", log.type === "payment_decline" ? "Payment decline" : category.label],
              ["Environment", log.environment],
              ["Request id", mono(log.correlationId), log.correlationId ?? undefined],
              ...(Object.entries(log.refs ?? {}).filter(([key]) => key !== "policyRef").map(([key, value]) => [key, mono(value), value]) as [string, JSX.Element, string][]),
              ["User", data.userId ? mono(`${data.userId}${data.userType ? ` · ${data.userType}` : ""}`) : null],
              ["IP", mono(data.ip)],
              ["Time", formatDateTime(log.createdAt)],
            ]}
          />
          {log.issue ? (
            <>
              <PanelTitle>This incident</PanelTitle>
              <Fields
                rows={[
                  ["Times seen", <b>{log.issue.count.toLocaleString()}</b>],
                  ["First seen", formatDateTime(log.issue.firstSeen)],
                  ["Last seen", <TimeAgo value={log.issue.lastSeen} />],
                  ["State", <span className="capitalize">{log.issue.state}</span>],
                ]}
              />
            </>
          ) : null}
          {related.length ? (
            <>
              <PanelTitle>Same request ({related.length} more)</PanelTitle>
              <ul className="glass divide-y divide-edge overflow-hidden p-0">
                {related.map((item) => (
                  <li key={item.id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                    <span className="font-mono text-[12px] font-semibold text-rose-600 dark:text-rose-300">{item.statusCode ?? "—"}</span>
                    <span className="min-w-0 flex-1 truncate font-[Gilroy-Medium] text-heading-color">{logName(item)} · {item.message}</span>
                    <span className="font-[Gilroy-Medium] text-[12px] text-label-muted">{formatDateTime(item.createdAt)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          <JsonView title="Other details" value={rest} />
        </>
      ) : null}

      {tab === "request" ? (
        <>
          <Fields rows={[["Method", mono(request.method ?? log.link.match(/^route:([A-Z]+)/)?.[1])], ["URL", mono(data.url ?? data.requestUrl)]]} />
          <JsonView title="Headers" value={request.headers} />
          <JsonView title="Path params" value={request.params} />
          <JsonView title="Query" value={request.query} />
          <JsonView title="Body" value={request.body} />
          {!request.headers && !request.body && !request.query ? <p className="text-[13px] text-light-gray">This platform didn't send request details (it needs the updated SDK).</p> : null}
        </>
      ) : null}

      {tab === "response" ? (
        <>
          <Fields rows={[["Status code", log.statusCode], ["Response time", log.responseTime != null ? `${log.responseTime} ms` : null]]} />
          <JsonView title="Client was told" value={(clientLog?.data as any)?.response?.body} />
          {providerLogs.map((item) => (
            <JsonView key={item.id} title={`Provider said · ${logName(item)}`} value={(item.data as any).providerResponse} />
          ))}
          <JsonView title="Response headers" value={response?.headers} />
          {!clientLog && !providerLogs.length ? <p className="text-[13px] text-light-gray">No response details were sent for this log.</p> : null}
        </>
      ) : null}
    </>
  );
}
